import { axiosInstance } from '../../App'
import {
  MedicalSpecialityItemDTO,
  MedicalSpecialityProfileDTO,
  MedicalSpecialitySavePayloadDTO,
} from '../../dto/medicalspecialities/MedicalSpecialitiesDTO'

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : -1
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : -1
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 't' || normalized === 'yes'
  }
  return false
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value : String(value ?? '')
}

function normalizeMedicalSpeciality(raw: unknown): MedicalSpecialityItemDTO | null {
  if (typeof raw !== 'object' || raw === null) return null
  const item = raw as Record<string, unknown>
  const id = toNumber(item.medicalspeciality)
  if (id <= 0) return null

  return {
    medicalspeciality: id,
    name: toStringSafe(item.name),
    systemspeciality: toBoolean(item.systemspeciality ?? item.systemSpeciality),
    allowsprescription: toBoolean(item.allowsprescription ?? item.allowsPrescription),
    entitytag: toNumber(item.entitytag ?? item.entityTag),
    entitytagdesc: toStringSafe(item.entitytagdesc ?? item.entityTagDesc),
  }
}

function normalizeProfile(raw: unknown): MedicalSpecialityProfileDTO | null {
  if (typeof raw !== 'object' || raw === null) return null
  const item = raw as Record<string, unknown>
  const id = toNumber(item.id)
  if (id <= 0) return null
  return {
    id,
    name: toStringSafe(item.name),
  }
}

export function useMedicalSpecialitiesService() {
async function getMedicalSpecialities(): Promise<MedicalSpecialityItemDTO[]> {
  const response = await axiosInstance.get('/api/secured/medicalspecialities/list')
  const array = Array.isArray(response.data) ? response.data : []
  return array
    .map(normalizeMedicalSpeciality)
    .filter((item): item is MedicalSpecialityItemDTO => item !== null)
}

async function getMedicalSpecialityById(id: number): Promise<MedicalSpecialityItemDTO> {
  const response = await axiosInstance.get(`/api/secured/medicalspecialities/${id}`)
  const normalized = normalizeMedicalSpeciality(response.data)
  if (!normalized) {
    throw new Error('Medical speciality detail payload is invalid')
  }
  return normalized
}

async function getMedicalSpecialityProfiles(): Promise<MedicalSpecialityProfileDTO[]> {
  const response = await axiosInstance.get('/api/secured/medicalspecialities/profiles')
  const array = Array.isArray(response.data) ? response.data : []
  return array
    .map(normalizeProfile)
    .filter((item): item is MedicalSpecialityProfileDTO => item !== null)
}

async function createMedicalSpeciality(payload: MedicalSpecialitySavePayloadDTO): Promise<void> {
  await axiosInstance.post('/api/secured/medicalspecialities', payload)
}

async function updateMedicalSpeciality(
  id: number,
  payload: MedicalSpecialitySavePayloadDTO,
): Promise<void> {
  await axiosInstance.put(`/api/secured/medicalspecialities/${id}`, payload)
}

async function deleteMedicalSpeciality(id: number): Promise<void> {
  await axiosInstance.delete(`/api/secured/medicalspecialities/${id}`)
}

  return {
    getMedicalSpecialities,
    getMedicalSpecialityById,
    getMedicalSpecialityProfiles,
    createMedicalSpeciality,
    updateMedicalSpeciality,
    deleteMedicalSpeciality,
  }
}
